/**
 * The predefined enrollment windows, in chronological order. Mirrors the
 * `EducatorEnrollmentWindowType` schema (documented standalone in the OpenAPI
 * spec, so it is not emitted as a generated TS enum — `windowType` arrives as a
 * plain `string`). The type and the runtime guard derive from one tuple so they
 * can't drift.
 */
const WINDOW_TYPES = [
  'early_enrollment',
  'priority_enrollment',
  'general_enrollment',
  'final_pay_by',
  'closed',
] as const

export type EducatorEnrollmentWindowType = (typeof WINDOW_TYPES)[number]

const isKnownWindowType = (
  value: string,
): value is EducatorEnrollmentWindowType =>
  (WINDOW_TYPES as readonly string[]).includes(value)

interface ResolveEffectiveWindowTypeArgs {
  /** `currentWindow.windowType` from cohort details (unconstrained `string`). */
  windowType?: string
  /** Cohort `state` — used only as the `closed` safety floor. */
  state?: string
}

/**
 * Single source of truth for the effective enrollment window that drives BOTH
 * the hero strip and the CTA, so the two can never diverge.
 *
 * Resolution rule:
 *   1. A live, known window wins — even if `state` is already `closed` (an admin
 *      misconfig where a window is active means applications really are open).
 *   2. No window (absent/unknown) + `state === 'closed'` → `'closed'` floor, so
 *      we never render "Apply for Free" once enrollment has closed.
 *   3. Otherwise → `undefined` (legacy).
 */
export const resolveEffectiveWindowType = ({
  windowType,
  state,
}: ResolveEffectiveWindowTypeArgs):
  | EducatorEnrollmentWindowType
  | undefined => {
  if (windowType && isKnownWindowType(windowType)) return windowType
  if (state === 'closed') return 'closed'
  return undefined
}
